import {
  useEffect,
  useCallback,
  useState,
  useLayoutEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { removeFlashNotification } from 'src/redux/modules/flashNotification';
import { Typography } from 'src/latest/src/components';
import Avatar from 'src/components/base/Avatar/Avatar';
import logoNotif from 'src/assets/icons/logo-shape.svg';
import close from 'src/assets/icons/icon-close.svg';
import './flash-notification.scss';
import AppAlert from '../AppAlert/AppAlert';
import { ButtonVariant } from '../../types';

interface Notification {
  id: string;
  photoUrl?: string;
  message: any;
  isError?: boolean;
  duration?: number;
  dispatchClass?: string;
  userName?: string;
  hasButton?: boolean;
  buttonAction?: () => void;
  buttonLabel?: string;
  buttonVariant?: ButtonVariant;
  isWelcome?: boolean;
  isSticky?: boolean;
  Avatar?: any;
  dedupeKey?: string;
}

function getBaseTop(isMobile: boolean) {
  return isMobile ? 72 : 90;
}

function NotificationItem({
  notification,
  topOffset,
  onHeightChange,
}: {
  notification: Notification;
  topOffset: number;
  onHeightChange: (id: string, height: number) => void;
}) {
  const dispatch = useDispatch();
  const {
    id,
    photoUrl,
    message,
    isError,
    duration = 4000,
    dispatchClass,
    userName,
    hasButton,
    buttonAction,
    buttonLabel,
    buttonVariant,
    isWelcome,
    isSticky,
    Avatar: AvatarComponent,
  } = notification;
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message && !isSticky) {
      const timer = setTimeout(() => {
        dispatch(removeFlashNotification(id));
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, message, duration, isSticky, dispatch]);

  const handleClose = () => {
    dispatch(removeFlashNotification(id));
  };

  useLayoutEffect(() => {
    if (!itemRef.current) return;

    const measureHeight = () => {
      const height = itemRef.current?.getBoundingClientRect().height;

      if (height) onHeightChange(id, height);
    };

    measureHeight();

    const resizeObserver = new ResizeObserver(measureHeight);

    resizeObserver.observe(itemRef.current);

    return () => resizeObserver.disconnect();
  }, [id, message, onHeightChange]);

  return (
    <div
      ref={itemRef}
      className={`flash-notification-item ${
        hasButton ? 'flash-notification__with-button' : ''
      } ${
        !AvatarComponent && hasButton
          ? 'flash-notification__without-avatar'
          : ''
      } ${
        typeof message === 'string' && message.length <= 30
          ? 'flash-string'
          : ''
      } is-visible ${dispatchClass}`}
      style={{
        top: `${topOffset}px`,
        zIndex: 999999 - topOffset,
      }}
    >
      <AppAlert
        variant={isError ? 'danger' : 'success'}
        className="flash-notification__alert animateOpen"
        hasButton={hasButton}
        buttonLabel={buttonLabel}
        buttonAction={buttonAction}
        buttonVariant={buttonVariant}
      >
        <div className="flash-notification__content d-flex justify-content-around">
          {photoUrl ? (
            <Avatar
              className="logo-notif"
              fullName={userName || ''}
              photoUrl={photoUrl}
            />
          ) : (
            <img
              src={logoNotif}
              alt="logo notification"
              className="logo-notif"
            />
          )}
          <div
            className={`flash-notification__content-wrapper ${
              isWelcome ? 'flash-notification__content-wrapper-welcome' : ''
            }`}
          >
            {typeof message === 'string' ? (
              <Typography
                className="flash-notification__content-wrapper__text ms-2"
                color="color-27"
                variant="size-16"
                weight="regular"
              >
                {message}
              </Typography>
            ) : (
              message
            )}
          </div>
          {isSticky && (
            <span
              className="flash-notification__content-close cursor-pointer"
              onClick={handleClose}
            >
              <img src={close} alt="close" />
            </span>
          )}
        </div>
      </AppAlert>
    </div>
  );
}

export default function FlashNotification() {
  const { notifications } = useSelector(({ flashNotification }: any) => ({
    notifications: flashNotification.notifications || [],
  }));
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleHeightChange = useCallback((id: string, height: number) => {
    setHeights(prev => {
      if (prev[id] === height) return prev;

      return { ...prev, [id]: height };
    });
  }, []);

  const getTopOffsets = useCallback(() => {
    const baseTop = getBaseTop(isMobile);
    const gap = isMobile ? 12 : 20;
    const topOffsets: Record<string, number> = {};
    let cumulative = baseTop;

    for (const notif of notifications) {
      topOffsets[notif.id] = cumulative;
      const height = heights[notif.id] || 0;

      cumulative += height + gap;
    }

    return topOffsets;
  }, [notifications, heights, isMobile]);

  if (!notifications || notifications.length === 0) {
    return null;
  }

  const topOffsets = getTopOffsets();

  return (
    <div className="flash-notification-container">
      {notifications.map((notification: Notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          topOffset={topOffsets[notification.id]}
          onHeightChange={handleHeightChange}
        />
      ))}
    </div>
  );
}
